import { useState } from 'react';
import { useReferenceData } from '@/features/pipeline/useReferenceData';
import { type ApiInvestmentResponse, readApiETag } from './types';
import { type UpdateInvestmentForm, buildUpdateBody, useUpdateInvestment } from './useUpdateInvestment';

const LABEL = 'mb-1 block text-[11px] font-medium uppercase tracking-wide text-vega-muted';
const INPUT =
  'w-full rounded border border-vega-line bg-vega-surface px-2.5 py-2 text-[13px] text-vega-ink';

/** número → string pt-BR para input (2.1 → "2,1"); null → "". */
function numToInput(n: number | null, scale = 1): string {
  if (n == null) return '';
  const v = n * scale;
  return Number(v.toFixed(2)).toString().replace('.', ',');
}

function splitTags(raw: string): string[] {
  return raw
    .split(',')
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
}

/**
 * Formulário de edição da ficha do Investimento (DEC-040). Edita codinome, fundo, responsável,
 * tese principal, equipe, tags, MOIC e TIR esperados. NÃO edita cheque (rodadas), métricas
 * realizadas (desinvestimento) nem status. Salva via PUT /api/investments/{id} (If-Match).
 */
export function InvestmentEditForm({ detail, onDone }: { detail: ApiInvestmentResponse; onDone: () => void }) {
  const reference = useReferenceData();
  const update = useUpdateInvestment();

  const [form, setForm] = useState<UpdateInvestmentForm>({
    codename: detail.codename,
    fundId: detail.fundId,
    leadUserId: detail.leadUserId,
    primaryThesisId: detail.primaryThesisId,
    moicExpected: numToInput(detail.moicExpected),
    irrExpected: numToInput(detail.irrExpected, 100),
    checkDiscretionary: numToInput(detail.checkDiscretionaryBRL),
    checkCoinvestment: numToInput(detail.checkCoinvestmentBRL),
    teamUserIds: detail.teamUserIds,
    tags: detail.tags,
  });
  const [tagsRaw, setTagsRaw] = useState(detail.tags.join(', '));

  const users = reference.data?.users ?? [];
  const funds = reference.data?.funds ?? [];
  const theses = reference.data?.theses ?? [];

  const canSave =
    form.codename.trim().length > 0 && form.fundId != null && form.leadUserId !== '' && !update.isPending;

  const toggleTeam = (id: string) =>
    setForm((f) => ({
      ...f,
      teamUserIds: f.teamUserIds.includes(id)
        ? f.teamUserIds.filter((x) => x !== id)
        : [...f.teamUserIds, id],
    }));

  const onSave = () => {
    if (!canSave) return;
    update.mutate(
      { id: detail.id, etag: readApiETag(detail), body: buildUpdateBody({ ...form, tags: splitTags(tagsRaw) }) },
      { onSuccess: onDone },
    );
  };

  return (
    <div className="mt-2">
      <label className="mb-3 block">
        <span className={LABEL}>Codinome *</span>
        <input
          value={form.codename}
          onChange={(e) => setForm((f) => ({ ...f, codename: e.target.value }))}
          maxLength={200}
          className={INPUT}
        />
      </label>

      <div className="mb-3 grid grid-cols-2 gap-3">
        <label className="block">
          <span className={LABEL}>Fundo *</span>
          <select
            value={form.fundId ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, fundId: e.target.value === '' ? null : Number(e.target.value) }))}
            className={INPUT}
          >
            <option value="">Selecione…</option>
            {funds.map((fund) => (
              <option key={fund.id} value={fund.id}>
                {fund.name}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className={LABEL}>Responsável *</span>
          <select
            value={form.leadUserId}
            onChange={(e) => setForm((f) => ({ ...f, leadUserId: e.target.value }))}
            className={INPUT}
          >
            <option value="">Selecione…</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="mb-3 block">
        <span className={LABEL}>Tese principal</span>
        <select
          value={form.primaryThesisId ?? ''}
          onChange={(e) =>
            setForm((f) => ({ ...f, primaryThesisId: e.target.value === '' ? null : Number(e.target.value) }))
          }
          className={INPUT}
        >
          <option value="">Nenhuma</option>
          {theses.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      </label>

      <div className="mb-3">
        <span className={LABEL}>Equipe</span>
        <div className="max-h-32 overflow-y-auto rounded border border-vega-line p-1">
          {users
            .filter((u) => u.id !== form.leadUserId)
            .map((u) => {
              const checked = form.teamUserIds.includes(u.id);
              return (
                <button
                  type="button"
                  key={u.id}
                  onClick={() => toggleTeam(u.id)}
                  className="flex w-full items-center gap-2 rounded-[3px] px-2 py-1.5 text-left text-[12.5px] hover:bg-vega-bg-2"
                >
                  <span
                    className={`inline-flex h-[13px] w-[13px] items-center justify-center rounded-[2px] border ${
                      checked ? 'border-vega-ink bg-vega-ink' : 'border-vega-line'
                    }`}
                  >
                    {checked && <span className="h-1.5 w-1.5 rounded-[1px] bg-white" />}
                  </span>
                  <span className="flex-1">{u.name}</span>
                </button>
              );
            })}
        </div>
      </div>

      <label className="mb-3 block">
        <span className={LABEL}>Tags</span>
        <input
          value={tagsRaw}
          onChange={(e) => setTagsRaw(e.target.value)}
          placeholder="separadas por vírgula"
          className={INPUT}
        />
      </label>

      <div className="mb-4 grid grid-cols-2 gap-3">
        <label className="block">
          <span className={LABEL}>MOIC esp.</span>
          <input
            value={form.moicExpected}
            onChange={(e) => setForm((f) => ({ ...f, moicExpected: e.target.value }))}
            inputMode="decimal"
            placeholder="ex.: 2,1"
            className={INPUT}
          />
        </label>
        <label className="block">
          <span className={LABEL}>TIR esp. (%)</span>
          <input
            value={form.irrExpected}
            onChange={(e) => setForm((f) => ({ ...f, irrExpected: e.target.value }))}
            inputMode="decimal"
            placeholder="ex.: 22"
            className={INPUT}
          />
        </label>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3">
        <label className="block">
          <span className={LABEL}>Cheque discricionário (R$)</span>
          <input
            value={form.checkDiscretionary}
            onChange={(e) => setForm((f) => ({ ...f, checkDiscretionary: e.target.value }))}
            inputMode="decimal"
            placeholder="ex.: 180000000"
            className={INPUT}
          />
        </label>
        <label className="block">
          <span className={LABEL}>Cheque coinvestimento (R$)</span>
          <input
            value={form.checkCoinvestment}
            onChange={(e) => setForm((f) => ({ ...f, checkCoinvestment: e.target.value }))}
            inputMode="decimal"
            placeholder="ex.: 0"
            className={INPUT}
          />
        </label>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onDone}
          disabled={update.isPending}
          className="rounded border border-vega-line px-3 py-1.5 text-[12.5px] font-medium text-vega-muted disabled:opacity-60"
        >
          Cancelar
        </button>
        <div className="flex-1" />
        <button
          type="button"
          onClick={onSave}
          disabled={!canSave}
          className="rounded bg-vega-ink px-3 py-1.5 text-[12.5px] font-semibold text-vega-bg disabled:opacity-50"
        >
          {update.isPending ? 'Salvando…' : 'Salvar'}
        </button>
      </div>
    </div>
  );
}
